import { z } from "zod/v4";

import {
  RepairSweepStaleStatusList,
  RepairTargetTypeList,
} from "./repair-targets";
import { BridgeMutationRecoveryRefV1Schema } from "../bridge-mutation-receipt";
import {
  PauseCancelReasonDtoSchema,
  SubscriptionBillingProviderList,
} from "../../subscriptions";
import { StripeProductVersionList } from "../../../schemas/stripe";
import { StripeApiCreateSubscriptionSettingDtoSchema } from "../../stripe-api/subscription";

import {
  BillingAdminScheduleScenarioList,
  BillingAgencyLifecycleScenarioList,
  BillingCadenceList,
  type BillingCommandFamily,
  BillingCreateReactivateScenarioList,
  BillingEffectiveTimingList,
  BillingPauseCancelReleaseActionList,
  BillingPauseCancelReleaseScenarioList,
  BillingPolicyCommandFamilyList,
  BillingProductSetChangeScenarioList,
  BillingProductSetChangeTypeList,
  BillingProviderLifecycleActionList,
  BillingScheduledBoundaryReasonList,
  BillingScheduledFollowupCommandTypeList,
  BillingScheduledWakeupScenarioList,
  BillingSubscriptionLifecycleReviewTypeList,
  BillingSubscriptionLifecycleScenarioList,
  BillingTrialCommandTypeList,
  BillingTrialLifecycleScenarioList,
  ProviderConfirmationActionList,
} from "./catalog";
import { BillingPlanIntentRefV1Schema } from "./plan-intent-ref";
import { billingStateAssertionError } from "./state-assertion-error";

// 2100-01-01T00:00:00Z. Any epoch-seconds boundary beyond this is nonsense, and
// a value past ~8.64e12 makes `new Date(seconds * 1000).toISOString()` throw a
// RangeError deep inside policy resolution — an uncoded failure that retries
// forever rather than rejecting the command. Bound it where the value enters.
const MAX_BOUNDARY_EPOCH_SECONDS = 4_102_444_800;

// Epoch seconds naming a future billing boundary (a scheduled pause or cancel).
export const BillingBoundaryEpochSecondsSchema = z
  .number()
  .int()
  .positive()
  .max(MAX_BOUNDARY_EPOCH_SECONDS);

// Narrow product reference used at the command-payload / intent-payload boundary
// (HTTP ingress and intent normalization). Distinct from the catalog-enriched
// `BillingCatalogProductV1Schema`, which only appears inside policy facts where
// the policy-resolution port has joined ids against cached Stripe price data.
// Do not interchange these shapes; widening this schema would conflate two
// different layer responsibilities.
export const BillingProductRefSchema = z.object({
  productId: z.string().min(1),
});

export type BillingProductRef = z.infer<typeof BillingProductRefSchema>;

// A single entry in the desired Discount Stack written to a Stripe subscription's
// `discounts[]`. `{ coupon }` creates a new discount from a coupon id (the auto
// Bundle Discount, or a newly added Operator Coupon); `{ discount }` reuses an
// existing Stripe discount by id (a preserved Operator Coupon) so re-applying it
// does NOT reset a repeating coupon's expiry countdown. See ADR-0009.
export const StripeDesiredDiscountV1Schema = z.union([
  z.object({ coupon: z.string().min(1) }).strict(),
  z.object({ discount: z.string().min(1) }).strict(),
]);

export type StripeDesiredDiscountV1 = z.infer<
  typeof StripeDesiredDiscountV1Schema
>;

// Stripe caps a subscription at 20 items, so no legitimate product-set target
// exceeds it; the bound keeps member-supplied targets from fanning out
// downstream (price-ownership resolution, rejection-reason echo).
export const MAX_PRODUCT_SET_TARGET_PRICE_IDS = 20;

export const BillingProductSetTargetSchema = z.object({
  version: z.enum(StripeProductVersionList),
  priceIds: z
    .array(z.string().min(1).max(255))
    .min(1)
    .max(MAX_PRODUCT_SET_TARGET_PRICE_IDS),
  // Deprecated single-coupon slot (an "explicit" coupon that replaced the Bundle
  // Discount). Retained for backward compatibility; new callers use manualCouponIds.
  couponId: z.string().nullish(),
  // Ordered Operator Coupon ids to stack on top of the auto Bundle Discount.
  // undefined = preserve live operator coupons; [] = clear them; [a, b] = set
  // exactly in that order. The Bundle Discount is never sent by the client.
  // Set-exact: the caller must already know the full live stack, so this is
  // for admin/scheduling callers that read it off the resolved subscription.
  // A self-serve caller that only knows the one coupon it's adding should use
  // `addCouponId` instead — reconstructing the full stack from a single
  // best-coupon read model silently drops every other live operator coupon.
  manualCouponIds: z.array(z.string().min(1)).optional(),
  // A single Operator Coupon id to add on top of whatever operator coupons
  // are already live, resolved server-side against the live Discount Stack
  // (policy-resolution owns the union). Mutually exclusive with
  // `manualCouponIds` in intent — when both are present, `manualCouponIds`
  // wins, since a set-exact instruction is unambiguous about the caller's
  // knowledge of the full stack and an add instruction is not. Singular
  // because self-serve only ever stages one coupon at a time; a caller that
  // needs to add more than one already knows the full stack and uses
  // `manualCouponIds`.
  addCouponId: z.string().min(1).optional(),
});

export type BillingProductSetTarget = z.infer<
  typeof BillingProductSetTargetSchema
>;

/**
 * The same target once the runtime has resolved it against the live
 * subscription, rather than as a caller stated it.
 *
 * A caller asks to own a set, so asking to own nothing is malformed at the
 * edge and the lower bound rejects it there. A resolved target is a diff — a
 * scheduled module removal re-diffed at its boundary, say — and an empty
 * result is a real outcome the policy layer names
 * (`rejected_empty_product_set_target`, or a no-op when the org owns nothing
 * to delete). Keeping the lower bound here would fail the runtime state
 * contract before policy could say either, so only the upper bound survives.
 */
export const BillingResolvedProductSetTargetSchema =
  BillingProductSetTargetSchema.extend({
    priceIds: z
      .array(z.string().min(1).max(255))
      .max(MAX_PRODUCT_SET_TARGET_PRICE_IDS),
  });

export type BillingResolvedProductSetTarget = z.infer<
  typeof BillingResolvedProductSetTargetSchema
>;

export const BillingTrialStateSchema = z.object({
  trialStartedAt: z.string().optional(),
  trialEndsAt: z.string().optional(),
  extendedTrialEndsAt: z.string().optional(),
});

export type BillingTrialState = z.infer<typeof BillingTrialStateSchema>;

// Upper bound on an admin-granted module-trial length. Without it an unbounded
// `trialDurationDays` (or far-future `trialEndAt`) grants effectively-free access
// to a paid module, so both the relative duration and the resolved trial end are
// capped at this horizon (ADR 0013 / ENG-6669).
export const MAX_MODULE_TRIAL_DURATION_DAYS = 365;

// Which channel issued a module-trial grant: the automated post-conversion
// cascade or a CS admin acting on one org.
export const BillingModuleTrialGrantTriggerList = [
  "module_cascade",
  "manual_admin",
] as const;

export type BillingModuleTrialGrantTrigger =
  (typeof BillingModuleTrialGrantTriggerList)[number];

// Which cascade condition fired, keyed on the paid modules the org held when
// the grant was decided. Only a `module_cascade` grant carries one.
export const BillingModuleTrialCascadeBranchList = [
  "content_only",
  "content_plus_other_module",
  "content_plus_checkout_and_pp",
] as const;

export type BillingModuleTrialCascadeBranch =
  (typeof BillingModuleTrialCascadeBranchList)[number];

// Marks an `add_module` product-set change as a per-module trial grant
// (ADR 0013 / ENG-6669). One marker per trialed module; a command may carry
// several, all attached together. Each carries its admin-selected trial length
// — either an absolute `trialEndAt` (ISO-8601) or a relative
// `trialDurationDays`, defaulting downstream to the module's `freeTrialDays`
// when neither is set — plus the Smart-Pricing confirmation flag the FE sets
// when the merchant accepts the commitment-on-conversion terms. Absent on
// non-trial product-set changes.
export const BillingModuleTrialMarkerSchema = z.object({
  // The price id of the module this marker grants a trial on. Carried
  // explicitly end-to-end so the backend binds the Trial Offer to exactly this
  // module rather than inferring the target by set-difference — inference
  // mis-binds the trial when subscription membership changes between the admin
  // staging the grant and the command executing (ADR 0013 / ENG-6669).
  trialedPriceId: z.string().min(1),
  trialEndAt: z.iso.datetime().optional(),
  trialDurationDays: z
    .number()
    .int()
    .positive()
    .max(MAX_MODULE_TRIAL_DURATION_DAYS)
    .optional(),
  smartPricingTrialConfirmed: z.boolean().optional(),
  // Attribution for the grant analytics event. Optional so commands persisted
  // before these fields existed still parse; the backend stamps both on every
  // grant it issues.
  trigger: z.enum(BillingModuleTrialGrantTriggerList).optional(),
  branch: z.enum(BillingModuleTrialCascadeBranchList).optional(),
});

export type BillingModuleTrialMarker = z.infer<
  typeof BillingModuleTrialMarkerSchema
>;

// The explicit counterpart to BillingModuleTrialMarkerSchema (ADR 0013):
// grant names the module it starts a trial on; revoke names the module it
// deliberately ends one on. Carried through a scheduled_wakeup resume's
// followupPayload so evaluateShopifyTrialRetention can tell a deliberate,
// commanded trial revocation apart from any other command that would
// otherwise accidentally drop a trialing price from its target.
export const ModuleTrialRevokeMarkerSchema = z.object({
  revokedPriceId: z.string().min(1),
});

export type ModuleTrialRevokeMarker = z.infer<
  typeof ModuleTrialRevokeMarkerSchema
>;

// The merchant's explicit "keep this module past its trial". Target membership
// cannot carry this: the target is declarative, so a trialing price must stay in
// it on every command just to avoid deleting the item, which makes presence
// useless as a confirmation signal. Policy resolution verifies the named price is
// genuinely trialing and genuinely has a parked cancellation before releasing.
export const ModuleTrialConfirmMarkerSchema = z.object({
  confirmedPriceId: z.string().min(1),
});

export type ModuleTrialConfirmMarker = z.infer<
  typeof ModuleTrialConfirmMarkerSchema
>;

// The inverse of the confirm marker: the merchant no longer wants the module
// once its trial ends. The product set does not change — the module keeps
// running free until the trial end — so only this marker distinguishes the
// command from a no-op. Policy resolution re-parks the cancellation the
// confirmation released.
export const ModuleTrialUnconfirmMarkerSchema = z.object({
  unconfirmedPriceId: z.string().min(1),
});

export type ModuleTrialUnconfirmMarker = z.infer<
  typeof ModuleTrialUnconfirmMarkerSchema
>;

export const BillingTierSnapshotRefSchema = z.object({
  billingOrderTierSnapshotId: z.string().uuid().optional(),
  tierName: z.string().optional(),
  locked: z.boolean().optional(),
});

export type BillingTierSnapshotRef = z.infer<
  typeof BillingTierSnapshotRefSchema
>;

export const BillingRegimeConversionContextSchema = z.object({
  kind: z.literal("legacy_to_modular_2026"),
  sourceBillingRegime: z.enum(["legacy_v1", "stripe_v2"]),
  targetBillingRegime: z.literal("modular_2026"),
  targetCatalogVersion: z.literal("2026_module_v1"),
  cutover: z.literal("after_success"),
});

export type BillingRegimeConversionContext = z.infer<
  typeof BillingRegimeConversionContextSchema
>;

export const BillingActiveSubscriptionSnapshotRefSchema = z.object({
  subscriptionId: z.string().uuid().optional(),
  stripeSubscriptionLinkId: z.string().uuid().optional(),
  billingProvider: z.enum(SubscriptionBillingProviderList).optional(),
  cadence: z.enum(BillingCadenceList).optional(),
});

export type BillingActiveSubscriptionSnapshotRef = z.infer<
  typeof BillingActiveSubscriptionSnapshotRefSchema
>;

export const ProviderLifecycleActionSchema = z.object({
  provider: z.literal("shopify"),
  action: z.enum(BillingProviderLifecycleActionList),
  // "provider" = Shopify already applied the lifecycle change (webhook-driven);
  // "api" = an admin/member command initiates it, so the executor must also
  // cancel the live Shopify AppSubscription. Absent = "provider" (persisted
  // payloads predate this field).
  origin: z.enum(["provider", "api"]).optional(),
  sourceEventId: z.string().min(1),
  shopifyWebhookTopic: z.string().min(1).optional(),
  shopifyWebhookId: z.string().min(1).optional(),
  shopifyShopId: z.string().min(1).optional(),
  shopifyAppSubscriptionId: z.string().min(1).optional(),
  shopifySubscriptionStatus: z.string().min(1).optional(),
  reason: z.string().min(1),
  // Wire-compatibility only — nothing reads it. Deployed readers that predate
  // the lifecycle-verb widening require this literal, so every `action: "cancel"`
  // payload must keep carrying it until those readers are gone; the new verbs
  // (`schedule_cancel`, `release`) are unparseable by them regardless, so their
  // producers must be deployed after the consumers that understand them.
  stripeCancelTiming: z.literal("at_period_end").optional(),
  // Epoch seconds for a schedule_cancel: the bridge Stripe subscription gets
  // cancel_at at this moment (absent = cancel at period end). Ignored by the
  // other verbs.
  cancelAt: z.number().optional(),
});

export type ProviderLifecycleAction = z.infer<
  typeof ProviderLifecycleActionSchema
>;

export const AgencyLifecyclePayloadSchema = z.object({
  scenario: z.enum(BillingAgencyLifecycleScenarioList),
  agencyId: z.string().min(1),
  orgId: z.string().min(1),
  subscriptionId: z.string().min(1).optional(),
  productId: z.string().min(1).optional(),
  sourcePlanId: z.string().min(1).optional(),
  destinationPlanId: z.string().min(1).optional(),
  dueAt: z.string().min(1).optional(),
});

export type AgencyLifecyclePayload = z.infer<
  typeof AgencyLifecyclePayloadSchema
>;

export const PauseCancelReleaseFollowupPayloadSchema = z.object({
  lifecycleAction: z.enum(BillingPauseCancelReleaseActionList),
  providerLifecycle: ProviderLifecycleActionSchema.optional(),
});

export type PauseCancelReleaseFollowupPayload = z.infer<
  typeof PauseCancelReleaseFollowupPayloadSchema
>;

/**
 * A scheduled module removal names only the prices it drops, never the product
 * set it expects to be left with. The boundary re-diffs those prices against the
 * live subscription, so anything the merchant adds inside the deferral window
 * survives — a frozen target would delete it alongside the removed module.
 */
export const ModuleRemovalFollowupPayloadSchema = z.object({
  removedPriceIds: z.array(z.string().min(1)).min(1),
});

export type ModuleRemovalFollowupPayload = z.infer<
  typeof ModuleRemovalFollowupPayloadSchema
>;

export const BillingFollowupPayloadSchema = z.object({
  resumeCommandFamily: z.enum(BillingPolicyCommandFamilyList),
  resumeScenario: z.string().min(1),
  requestedProductSetTarget: BillingProductSetTargetSchema.optional(),
  requestedCadence: z.enum(BillingCadenceList).optional(),
  requestedEffectiveTiming: z.enum(BillingEffectiveTimingList).optional(),
  activeProducts: z.array(BillingProductRefSchema).optional(),
  trialState: BillingTrialStateSchema.optional(),
  currentTierSnapshot: BillingTierSnapshotRefSchema.optional(),
  currentCadence: z.enum(BillingCadenceList).optional(),
  agencyLifecycle: AgencyLifecyclePayloadSchema.optional(),
  pauseCancelRelease: PauseCancelReleaseFollowupPayloadSchema.optional(),
  moduleRemoval: ModuleRemovalFollowupPayloadSchema.optional(),
  moduleTrialRevoke: ModuleTrialRevokeMarkerSchema.optional(),
  /**
   * The command whose pending downstream-effect rows a retry-backoff wakeup
   * exists to re-deliver. Named explicitly rather than inferred from the
   * wakeup's source command, because a retry that fails again schedules a
   * further wakeup off *itself*: without this the second resume would look for
   * pending rows on the first resume, which owns none, and the debt would be
   * silently dropped.
   */
  effectRetryOfCommandId: z.string().min(1).optional(),
});

export type BillingFollowupPayload = z.infer<
  typeof BillingFollowupPayloadSchema
>;

// The boundary follow-up a two-phase internal plan schedules alongside its
// immediate lifecycle action. Shared verbatim by the execution-plan envelope and
// the runtime checkpoint: a field added here must survive the checkpoint
// round-trip, so both sites reference this schema rather than restating it.
export const BillingScheduledWakeupV1Schema = z.object({
  dueAt: z.string().min(1),
  deferredBoundaryReason: z.enum(BillingScheduledBoundaryReasonList),
  followupCommandType: z.enum(BillingScheduledFollowupCommandTypeList),
  idempotencyKey: z.string().min(1),
  followupPayload: BillingFollowupPayloadSchema.optional(),
});

export type BillingScheduledWakeupV1 = z.infer<
  typeof BillingScheduledWakeupV1Schema
>;

// The inverse of a scheduled wakeup: a plan revoking a boundary another
// command booked. Naming the source command and the job id on the plan is what
// keeps the release an executed plan effect rather than a side effect of
// policy resolution. Shared by the execution-plan envelope and the runtime
// checkpoint, so the releasing command's own record names what it released.
export const BillingReleaseScheduledWakeupV1Schema = z.object({
  sourceCommandId: z.string().min(1),
  delayedJobId: z.string().min(1),
});

export type BillingReleaseScheduledWakeupV1 = z.infer<
  typeof BillingReleaseScheduledWakeupV1Schema
>;

export const BillingAdminSettingsSchema =
  StripeApiCreateSubscriptionSettingDtoSchema;

export const BillingAdminPhaseItemSchema = z.object({
  priceId: z.string().min(1),
  productId: z.string().min(1),
  quantity: z.number(),
  version: z.enum(StripeProductVersionList),
  id: z.string().min(1).optional(),
  deleted: z.boolean().optional(),
});

export const BillingAdminPhaseSchema = z.object({
  phaseStart: z.number(),
  phaseEnd: z.number().nullish(),
  trialEndTs: z.number().nullish(),
  // Deprecated single-coupon slot. Retained for backward compatibility; the auto
  // Bundle Discount plus operator coupons now travel via desiredDiscounts (resolved)
  // and manualCouponIds (client input).
  couponId: z.string().min(1).nullish(),
  // Client input: Operator Coupon ids to stack on top of the auto Bundle Discount
  // for this phase. undefined = preserve live operator coupons; [] = clear them;
  // [a, b] = set exactly in that order. The Bundle Discount is never sent by the client.
  manualCouponIds: z.array(z.string().min(1)).optional(),
  // Resolved by policy-resolution: the full ordered desired Discount Stack for this
  // phase (Bundle Discount at #0, then operator coupons). provider-execution writes
  // it verbatim to the schedule phase's discounts[].
  desiredDiscounts: z.array(StripeDesiredDiscountV1Schema).optional(),
  items: z.array(BillingAdminPhaseItemSchema).min(1),
});

export const AdminScheduleBillingCommandPayloadV1Schema = z.object({
  commandFamily: z.literal("admin_schedule"),
  planIntentId: z.uuid().optional(),
  requestedPlanIntentId: z.uuid().optional(),
  scenario: z.enum(BillingAdminScheduleScenarioList),
  requestedProductSetTarget: BillingProductSetTargetSchema,
  requestedCadence: z.enum(BillingCadenceList),
  billingProvider: z.enum(SubscriptionBillingProviderList),
  billingProviderSelection: z.enum(SubscriptionBillingProviderList).optional(),
  customerId: z.string().min(1),
  agencyId: z.string().nullish(),
  stripeSubscriptionId: z.string().min(1).optional(),
  subscriptionLinkIds: z.array(z.string().min(1)).min(1).optional(),
  settings: BillingAdminSettingsSchema,
  phases: z.array(BillingAdminPhaseSchema).min(1),
  billingRegimeConversion: BillingRegimeConversionContextSchema.optional(),
});

export const AgencyLifecycleBillingCommandPayloadV1Schema =
  AgencyLifecyclePayloadSchema.extend({
    commandFamily: z.literal("agency_lifecycle"),
    resolvedBillingProvider: z.literal("stripe"),
    billingRegimeConversion: BillingRegimeConversionContextSchema.optional(),
  });

export const CreateReactivateBillingCommandPayloadV1Schema = z.object({
  commandFamily: z.literal("create_reactivate"),
  planIntentId: z.uuid().optional(),
  requestedPlanIntentId: z.uuid().optional(),
  scenario: z.enum(BillingCreateReactivateScenarioList),
  requestedProductSetTarget: BillingProductSetTargetSchema,
  requestedCadence: z.enum(BillingCadenceList),
  trialDaysRequested: z.number().nullish(),
  billingProviderSelection: z.enum(SubscriptionBillingProviderList).optional(),
  existingBillingProvider: z.enum(SubscriptionBillingProviderList).optional(),
  stripeSubscriptionLinkId: z.string().uuid().optional(),
  billingRegimeConversion: BillingRegimeConversionContextSchema.optional(),
});

export const TrialLifecycleBillingCommandPayloadV1Schema = z.object({
  commandFamily: z.literal("trial_lifecycle"),
  scenario: z.enum(BillingTrialLifecycleScenarioList),
  trialCommandType: z.enum(BillingTrialCommandTypeList),
  activeProducts: z.array(BillingProductRefSchema).optional(),
  trialState: BillingTrialStateSchema.optional(),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
});

export const ProductSetChangeBillingCommandPayloadV1Schema = z
  .object({
    commandFamily: z.literal("product_set_change"),
    planIntentId: z.uuid().optional(),
    requestedPlanIntentId: z.uuid().optional(),
    scenario: z.enum(BillingProductSetChangeScenarioList),
    changeType: z.enum(BillingProductSetChangeTypeList).optional(),
    requestedProductSetTarget: BillingProductSetTargetSchema.optional(),
    requestedEffectiveTiming: z.enum(BillingEffectiveTimingList),
    resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
    stripeSubscriptionLinkId: z.string().uuid(),
    moduleTrials: BillingModuleTrialMarkerSchema.array().min(1).optional(),
    moduleTrialConfirm: ModuleTrialConfirmMarkerSchema.array()
      .min(1)
      .optional(),
    moduleTrialRevoke: ModuleTrialRevokeMarkerSchema.array().min(1).optional(),
    moduleTrialUnconfirm: ModuleTrialUnconfirmMarkerSchema.array()
      .min(1)
      .optional(),
    // The pre-multi singular marker. The schema is non-strict, so without this
    // guard a payload persisted before the moduleTrials cutover would replay
    // with its marker silently stripped — degrading the grant to a full-price
    // billed add (ENG-6669). Reject loudly instead; the admin re-issues.
    moduleTrial: z.never().optional(),
    billingRegimeConversion: BillingRegimeConversionContextSchema.optional(),
    // Operator-authored order-volume quantity for a modular Shopify bridge.
    // Written only by the admin adjust-billing lane; normalization rejects it
    // on any non-admin request source.
    orderVolumeQuantity: z.number().int().positive().optional(),
    // The deferred-removal command whose scheduled-wakeup booking this undo
    // releases, carried from `scheduledModuleRemovals[].commandId`. Addressing
    // the removal by command rather than by module keeps concurrent removals
    // distinguishable.
    sourceCommandId: z.string().min(1).optional(),
  })
  .superRefine((value, ctx) => {
    const undoesPendingRemoval =
      value.scenario === "cancel_pending_module_removal";
    const carriesNoChangeType =
      undoesPendingRemoval ||
      value.scenario === "apply_coupon" ||
      value.scenario === "commit_trial_plan";

    if (undoesPendingRemoval !== !!value.sourceCommandId) {
      ctx.addIssue({
        code: "custom",
        path: ["sourceCommandId"],
        message:
          "sourceCommandId is required by cancel_pending_module_removal and rejected on every other product-set-change scenario",
      });
    }

    if (carriesNoChangeType === !!value.changeType) {
      ctx.addIssue({
        code: "custom",
        path: ["changeType"],
        message:
          "changeType is required by every product-set change and rejected on cancel_pending_module_removal, apply_coupon and commit_trial_plan",
      });
    }
  });

export const PauseCancelReleaseBillingCommandPayloadV1Schema = z
  .object({
    commandFamily: z.literal("pause_cancel_release"),
    planIntentId: z.uuid().optional(),
    scenario: z.enum(BillingPauseCancelReleaseScenarioList),
    lifecycleAction: z.enum(BillingPauseCancelReleaseActionList),
    activeSubscriptionSnapshot:
      BillingActiveSubscriptionSnapshotRefSchema.optional(),
    resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
    stripeSubscriptionLinkId: z.string().uuid().optional(),
    reasons: z.array(PauseCancelReasonDtoSchema),
    returnDate: z.number().nullish(),
    comments: z.string().nullish(),
    providerLifecycle: ProviderLifecycleActionSchema.optional(),
    // A request, not a timing: the pause lands on the first period end at or
    // after it (ADR-0037).
    pauseAt: z.number().optional(),
    cancelAt: z.number().optional(),
    // A pause always waits for its billing boundary, so no timing is the
    // caller's to choose. The schema is non-strict, so without this guard a
    // timing would be silently stripped instead of refused; an operator who
    // must pause now uses admin_modify.
    requestedEffectiveTiming: z.never().optional(),
    // The parked pause whose scheduled-wakeup booking this undo releases,
    // carried from `pendingPause.commandId`.
    sourceCommandId: z.string().min(1).optional(),
  })
  .superRefine((value, ctx) => {
    if (
      (value.lifecycleAction === "transfer_to_pause" ||
        value.lifecycleAction === "cancel") &&
      value.reasons.length === 0
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["reasons"],
        message:
          "pause and cancel command payloads require at least one reason",
      });
    }

    if (
      (value.lifecycleAction === "cancel_pending_pause") !==
      !!value.sourceCommandId
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["sourceCommandId"],
        message:
          "sourceCommandId is required by cancel_pending_pause and rejected on every other pause/cancel/release action",
      });
    }
  });

export const SubscriptionLifecycleBillingCommandPayloadV1Schema = z.object({
  commandFamily: z.literal("subscription_lifecycle"),
  scenario: z.enum(BillingSubscriptionLifecycleScenarioList),
  lifecycleReviewType: z.enum(BillingSubscriptionLifecycleReviewTypeList),
  currentTierSnapshot: BillingTierSnapshotRefSchema.optional(),
  currentCadence: z.enum(BillingCadenceList).optional(),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
});

export const ScheduledWakeupBillingCommandPayloadV1Schema = z.object({
  commandFamily: z.literal("scheduled_wakeup"),
  scenario: z.enum(BillingScheduledWakeupScenarioList),
  delayedJobId: z.string().min(1),
  queueName: z.string().min(1),
  dueAt: z.string(),
  followupCommandType: z.enum(BillingScheduledFollowupCommandTypeList),
  resumeCommandFamily: z.enum(BillingPolicyCommandFamilyList),
  resumeScenario: z.string().min(1),
  followupPayload: BillingFollowupPayloadSchema.optional(),
  resolvedBillingProvider: z.enum(SubscriptionBillingProviderList),
  planIntentRef: BillingPlanIntentRefV1Schema.optional(),
});

export const ReconciliationBillingCommandPayloadV1Schema = z
  .object({
    commandFamily: z.literal("reconciliation"),
    stripeEventId: z.string().min(1).optional(),
    stripeEventType: z.string().min(1).optional(),
    stripeSubscriptionId: z.string().optional(),
    stripeCustomerId: z.string().optional(),
    providerEventSource: z.enum(["shopify_webhook"]).optional(),
    sourceEventId: z.string().min(1).optional(),
    shopifyWebhookTopic: z.string().min(1).optional(),
    shopifyWebhookId: z.string().min(1).optional(),
    shopifyShopId: z.string().min(1).optional(),
    shopifyAppSubscriptionId: z.string().min(1).optional(),
    shopifySubscriptionStatus: z.string().min(1).optional(),
    classificationKind: z
      .enum([
        "externally_executed",
        "stripe_initiated_lifecycle",
        "shopify_initiated_lifecycle",
      ])
      .optional(),
    classificationReason: z.string().min(1).optional(),
    driftCheckType: z
      .enum([
        "full",
        "orphan_webhook",
        "targeted_projection",
        "latest_plan_intent",
        "module_trial_settlement",
        "shopify_receivable_capacity",
      ])
      .optional(),
    // shopify_receivable_capacity: the Stripe invoice the charge poster could
    // not post because the usage cap could not absorb it. Reconciliation reads
    // the invoice live rather than trusting a queued amount snapshot.
    stripeInvoiceId: z.string().min(1).optional(),
    projectionRef: z.string().min(1).optional(),
    localProjectionSnapshot: z.record(z.string(), z.unknown()).optional(),
    authoritativeProviderSnapshot: z.record(z.string(), z.unknown()).optional(),
    authoritativeStripeSnapshot: z.record(z.string(), z.unknown()).optional(),
    driftHints: z.record(z.string(), z.unknown()).optional(),
    planIntentId: z.uuid().optional(),
    planRevisionHint: z.number().int().positive().optional(),
    shopifyCapApprovalId: z.uuid().optional(),
    billingRegimeConversion: BillingRegimeConversionContextSchema.optional(),
  })
  .superRefine((value, ctx) => {
    const hasLegacyWebhookShape =
      !!value.stripeEventId &&
      !!value.stripeEventType &&
      !!value.classificationKind &&
      !!value.classificationReason;
    const hasShopifyWebhookShape =
      value.providerEventSource === "shopify_webhook" &&
      !!value.shopifyWebhookTopic &&
      value.classificationKind === "shopify_initiated_lifecycle" &&
      !!value.classificationReason;
    const hasCanonicalShape = !!value.driftCheckType;

    if (
      !hasLegacyWebhookShape &&
      !hasShopifyWebhookShape &&
      !hasCanonicalShape
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "reconciliation payload must provide either legacy webhook fields or driftCheckType",
      });
    }
  });

const RepairSweepStaleCommandRefSchema = z.union([
  z.string().min(1),
  z.object({
    commandId: z.string().min(1),
    organizationId: z.string().min(1).optional(),
    expectedStatus: z.enum(RepairSweepStaleStatusList).optional(),
  }),
]);

export const RepairSweepBillingCommandPayloadV1Schema = z
  .object({
    commandFamily: z.literal("repair_sweep"),
    repairTargetType: z.enum(RepairTargetTypeList).optional(),
    staleCommandRefs: z.array(RepairSweepStaleCommandRefSchema).optional(),
    olderThanMinutes: z.number().int().positive().optional(),
    limit: z.number().int().positive().optional(),
    bridgeMutationRecoveryRef: BridgeMutationRecoveryRefV1Schema.optional(),
  })
  .catchall(z.unknown());

export const ProviderConfirmationBillingCommandPayloadV1Schema = z.object({
  commandFamily: z.literal("provider_confirmation"),
  action: z.enum(ProviderConfirmationActionList),
  provider: z.literal("stripe"),
  originalCommandId: z.string().min(1),
  paymentIntentId: z.string().min(1),
  triggerEventId: z.string().min(1).optional(),
  triggerEventType: z.string().min(1).optional(),
});

export const ShopifyCapApprovalBillingCommandPayloadV1Schema = z.object({
  commandFamily: z.literal("shopify_cap_approval"),
  action: z.literal("request"),
  planIntentId: z.uuid(),
  approvalRevision: z.number().int().positive(),
  triggerPlanRevision: z.number().int().positive(),
  billingRegimeConversion: BillingRegimeConversionContextSchema.optional(),
});

export const BillingCommandPayloadV1Schema = z.discriminatedUnion(
  "commandFamily",
  [
    CreateReactivateBillingCommandPayloadV1Schema,
    TrialLifecycleBillingCommandPayloadV1Schema,
    ProductSetChangeBillingCommandPayloadV1Schema,
    PauseCancelReleaseBillingCommandPayloadV1Schema,
    SubscriptionLifecycleBillingCommandPayloadV1Schema,
    ScheduledWakeupBillingCommandPayloadV1Schema,
    ReconciliationBillingCommandPayloadV1Schema,
    RepairSweepBillingCommandPayloadV1Schema,
    AdminScheduleBillingCommandPayloadV1Schema,
    AgencyLifecycleBillingCommandPayloadV1Schema,
    ProviderConfirmationBillingCommandPayloadV1Schema,
    ShopifyCapApprovalBillingCommandPayloadV1Schema,
  ]
);

export type CreateReactivateBillingCommandPayloadV1 = z.infer<
  typeof CreateReactivateBillingCommandPayloadV1Schema
>;

export type TrialLifecycleBillingCommandPayloadV1 = z.infer<
  typeof TrialLifecycleBillingCommandPayloadV1Schema
>;

export type ProductSetChangeBillingCommandPayloadV1 = z.infer<
  typeof ProductSetChangeBillingCommandPayloadV1Schema
>;

export type PauseCancelReleaseBillingCommandPayloadV1 = z.infer<
  typeof PauseCancelReleaseBillingCommandPayloadV1Schema
>;

export type SubscriptionLifecycleBillingCommandPayloadV1 = z.infer<
  typeof SubscriptionLifecycleBillingCommandPayloadV1Schema
>;

export type ScheduledWakeupBillingCommandPayloadV1 = z.infer<
  typeof ScheduledWakeupBillingCommandPayloadV1Schema
>;

export type ReconciliationBillingCommandPayloadV1 = z.infer<
  typeof ReconciliationBillingCommandPayloadV1Schema
>;

export type RepairSweepBillingCommandPayloadV1 = z.infer<
  typeof RepairSweepBillingCommandPayloadV1Schema
>;

export type AdminScheduleBillingCommandPayloadV1 = z.infer<
  typeof AdminScheduleBillingCommandPayloadV1Schema
>;

export type AgencyLifecycleBillingCommandPayloadV1 = z.infer<
  typeof AgencyLifecycleBillingCommandPayloadV1Schema
>;

export type ProviderConfirmationBillingCommandPayloadV1 = z.infer<
  typeof ProviderConfirmationBillingCommandPayloadV1Schema
>;

export type ShopifyCapApprovalBillingCommandPayloadV1 = z.infer<
  typeof ShopifyCapApprovalBillingCommandPayloadV1Schema
>;

type BillingCommandPayloadV1Union = z.infer<
  typeof BillingCommandPayloadV1Schema
>;

export type BillingCommandPayloadV1<
  TCommandFamily extends BillingCommandFamily = BillingCommandFamily,
> = Extract<BillingCommandPayloadV1Union, { commandFamily: TCommandFamily }>;

export function isBillingCommandPayload(
  value: unknown
): value is BillingCommandPayloadV1 {
  return BillingCommandPayloadV1Schema.safeParse(value).success;
}

export function assertBillingCommandPayload(
  value: unknown
): asserts value is BillingCommandPayloadV1 {
  const parsed = BillingCommandPayloadV1Schema.safeParse(value);
  if (!parsed.success) {
    throw billingStateAssertionError(
      "billing command payload is missing required fields",
      parsed.error
    );
  }
}
